import { useQuery } from '@tanstack/react-query'
import { ArrowDownRight, ArrowUpRight, Clock3, Fingerprint, Gauge, KeyRound, ShieldCheck, Sparkles } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Header } from '../components/Header'
import { ProductGrid } from '../components/ProductGrid'
import { ProductImage } from '../components/ProductImage'
import { useReducedMotion } from '../hooks/useReducedMotion'
import { apiRequest } from '../lib/api'
import type { Product } from '../types'

function HeroVisual({ products }: { products: Product[] }) {
  const reducedMotion = useReducedMotion()
  const [index, setIndex] = useState(0)
  const active = products[index % products.length] ?? products[0]

  useEffect(() => {
    if (reducedMotion || products.length <= 1) return
    const timer = window.setInterval(() => setIndex((value) => (value + 1) % products.length), 3200)
    return () => window.clearInterval(timer)
  }, [reducedMotion, products.length])

  if (!active) return null
  const status = active.weao ? (active.weao.working ? 'WORKING' : 'PATCHED') : 'READY'

  return (
    <div className="hero__visual" aria-hidden="true">
      <div className="eclipse-device" data-working={active.weao ? active.weao.working : true} style={{ ['--hero-accent' as string]: active.accent }}>
        <div className="eclipse-device__grid" />
        <div className="eclipse-device__halo" />
        <div className="eclipse-device__disc">
          <ProductImage key={active.id} name={active.name} />
        </div>
        <div className="eclipse-device__tag eclipse-device__tag--top"><span>STATUS</span><strong>{status}</strong></div>
        <div className="eclipse-device__tag eclipse-device__tag--bottom"><span>{active.tag ? active.tag.toUpperCase() : 'BUILD'}</span><strong>{active.name.toUpperCase()} // {String(index % products.length + 1).padStart(2, '0')}</strong></div>
        <span className="eclipse-device__coordinate">43° 17′ 22″ N</span>
        {products.length > 1 ? (
          <div className="eclipse-device__dots">
            {products.map((product, dotIndex) => (
              <button
                key={product.id}
                type="button"
                aria-label={`Show ${product.name}`}
                data-active={dotIndex === index % products.length}
                onClick={() => setIndex(dotIndex)}
              />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  )
}

const fallbackProducts: Product[] = [
  {
    id: 'volt',
    slug: 'volt',
    name: 'Volt',
    tagline: 'Instant power. Absolute control.',
    description: 'Premium access to Volt with continuous updates, instant delivery and priority support from CheatsEclipse.',
    plans: ['weekly', 'monthly'],
    pricing: { weekly: 549, monthly: 1999, lifetime: 0, currency: 'USD' },
    stock: 0,
    stockByPlan: { weekly: 0, monthly: 0, lifetime: 0 },
    status: 'active',
    accent: '#8b5cf6',
    features: ['Instant activation', 'Continuous updates', 'Priority support', 'Automatic key delivery'],
    rating: 0,
    reviewsCount: 0,
    tag: 'Executor',
    weaoSlug: 'volt',
    weao: null,
    createdAt: '',
    updatedAt: '',
  },
]

export function HomePage() {
  const productsQuery = useQuery({
    queryKey: ['products'],
    queryFn: () => apiRequest<{ data: Product[] }>('/api/products'),
    placeholderData: { data: fallbackProducts },
  })
  const products = productsQuery.data?.data?.length ? productsQuery.data.data : fallbackProducts

  return (
    <div className="page-shell">
      <Header />
      <main>
        <section className="hero section-frame">
          <div className="hero__content t-stagger" data-visible="true">
            <div className="availability-pill">
              <span /> MARKET ONLINE <i>{String(products.length).padStart(2, '0')} {products.length === 1 ? 'PRODUCT' : 'PRODUCTS'}</i>
            </div>
            <h1>
              Access that<br />
              <span>changes the game.</span>
            </h1>
            <p className="hero__lead">Premium software, instant activation and an experience built for those who refuse the ordinary.</p>
            <div className="hero__actions">
              <a className="button button--primary" href="#products">Browse products <ArrowDownRight size={18} /></a>
              <Link className="button button--ghost" to="/account">My account</Link>
            </div>
            <div className="hero__proof">
              <div><strong>24/7</strong><span>Digital access</span></div>
              <i />
              <div><strong>Stripe</strong><span>Protected payment</span></div>
              <i />
              <div><strong>Clerk</strong><span>Secure identity</span></div>
            </div>
          </div>

          <HeroVisual products={products} />

          {productsQuery.isError ? <div className="api-notice">Preview mode — connect the API for live data.</div> : null}
        </section>

        <div className="signal-strip" aria-hidden="true">
          <div>
            <span>INSTANT ACCESS</span><i>✦</i><span>PROTECTED PAYMENT</span><i>✦</i><span>CONTINUOUS SUPPORT</span><i>✦</i><span>AUTO DELIVERY</span><i>✦</i>
            <span>INSTANT ACCESS</span><i>✦</i><span>PROTECTED PAYMENT</span><i>✦</i><span>CONTINUOUS SUPPORT</span><i>✦</i><span>AUTO DELIVERY</span><i>✦</i>
          </div>
        </div>

        <section className="product-section section-frame" id="products">
          <ProductGrid products={products} />
        </section>

        <section className="experience-section section-frame" id="experience">
          <div className="section-heading">
            <div><span className="eyebrow"><span>02</span> ECLIPSE SYSTEM</span><h2>Designed around<br /><em>trust.</em></h2></div>
            <p>Every layer — from the first pixel to renewal — is built so buying and managing your access stays simple, transparent and secure.</p>
          </div>

          <div className="bento-grid">
            <article className="bento-card bento-card--large">
              <div className="bento-card__icon"><Fingerprint /></div>
              <span className="micro-label">IDENTITY</span>
              <h3>Your access is yours alone.</h3>
              <p>Clerk protects every session and ties purchases, reviews and billing to a verified identity.</p>
              <div className="identity-visual"><span /><span /><span /><i /></div>
            </article>
            <article className="bento-card bento-card--metric">
              <Gauge />
              <strong>Instant</strong>
              <span>KEY DELIVERY</span>
              <div className="pulse-line"><i /><i /><i /><i /><i /></div>
            </article>
            <article className="bento-card">
              <div className="bento-card__icon"><ShieldCheck /></div>
              <span className="micro-label">CHECKOUT</span>
              <h3>Stripe, end to end.</h3>
              <p>CheatsEclipse never stores your card details.</p>
            </article>
            <article className="bento-card bento-card--wide">
              <div>
                <div className="bento-card__icon"><Clock3 /></div>
                <span className="micro-label">LIFECYCLE</span>
                <h3>Pay once. Get your key.</h3>
                <p>Every purchase is a one-time license — 1 week or 1 month, you choose. The key lands in your inbox and your account the moment payment clears. No subscriptions, nothing renews.</p>
              </div>
              <div className="lifecycle-visual">
                <span className="is-active"><i>1</i>Purchase</span><b /><span><i>2</i>Delivery</span><b /><span><i>3</i>Control</span>
              </div>
            </article>
            <article className="bento-card bento-card--accent">
              <Sparkles />
              <h3>One store.<br />One clear signal.</h3>
              <a href="#products">View products <ArrowUpRight size={16} /></a>
            </article>
          </div>
        </section>

        <section className="closing-section section-frame">
          <div className="closing-card">
            <div className="closing-card__mark"><img src="/logo.webp" alt="" /></div>
            <span className="eyebrow">READY WHEN YOU ARE</span>
            <h2>Enter the <em>eclipse.</em></h2>
            <p>Every product is a one-time 1-week or 1-month license. Pay once, no subscriptions.</p>
            <a className="button button--light" href="#products">Choose my plan <ArrowUpRight size={18} /></a>
          </div>
        </section>
      </main>

      <footer className="site-footer section-frame">
        <div className="brand"><span className="brand__mark"><img src="/logo.webp" alt="" /></span><span className="brand__name">CheatsEclipse</span></div>
        <p>Digital experiences out of orbit.</p>
        <div><Link to="/account"><KeyRound size={14} /> My account</Link><span>© {new Date().getFullYear()} CheatsEclipse</span></div>
      </footer>
    </div>
  )
}
